import React, { useCallback, useEffect, useState } from 'react';

import { Alert } from '@shared/components/bootstrap';

import { createUnavailability, destroyUnavailability, errorMessageFromResponse, updateUnavailability } from './api';
import { AvailabilityConfirmationDialog } from './confirmation_dialog';
import { DEFAULT_UNAVAILABILITY } from './constants';
import { ManagementCalendar } from './management_calendar';
import { NonrecurringTable } from './nonrecurring_table';
import { RecurringTable } from './recurring_table';
import { IAvailabilityFilters, IUnavailability, IUnavailabilityForm } from './types';

type ManagementViewProps = {
  filters: IAvailabilityFilters;
  unavailabilities: IUnavailability[] | null;
  loading: boolean;
  errorMessage?: string;
  onReload(): void;
  onClearError(): void;
  onError(message: string): void;
};

const pickAttributes = (form: IUnavailabilityForm): IUnavailability => ({
  id: form.id,
  user_id: form.user_id,
  reoccurring: form.reoccurring,
  forever: form.forever,
  start_date: form.start_date,
  until_date: form.until_date,
  mon: form.mon,
  tue: form.tue,
  wed: form.wed,
  thu: form.thu,
  fri: form.fri,
  sat: form.sat,
  sun: form.sun,
});

export const ManagementView: React.FC<ManagementViewProps> = ({
  filters,
  unavailabilities,
  loading,
  errorMessage,
  onReload,
  onClearError,
  onError,
}) => {
  const [selection, setSelection] = useState<IUnavailability | null>(null);
  const [form, setForm] = useState<IUnavailabilityForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [destroyTarget, setDestroyTarget] = useState<IUnavailability | null>(null);

  const cancel = useCallback(() => {
    setSelection(null);
    setForm(null);
  }, []);

  const add = useCallback(
    (reoccurring: boolean) => {
      setSelection(null);
      setForm({
        ...DEFAULT_UNAVAILABILITY,
        reoccurring,
        user_id: filters.user_id,
      });
      onClearError();
    },
    [filters.user_id, onClearError],
  );

  const manage = useCallback(
    (unavailability: IUnavailability) => {
      setSelection(unavailability);
      setForm({ ...unavailability });
      onClearError();
    },
    [onClearError],
  );

  const save = useCallback(async () => {
    if (!form || saving) {
      return;
    }

    setSaving(true);
    onClearError();

    try {
      const payload = pickAttributes(form);
      if (selection) {
        await updateUnavailability(payload);
      } else {
        await createUnavailability(payload);
      }
      cancel();
      onReload();
    } catch (error) {
      onError(errorMessageFromResponse(error));
    } finally {
      setSaving(false);
    }
  }, [cancel, form, onError, onReload, saving, selection]);

  const confirmDestroy = useCallback(async () => {
    if (!destroyTarget?.id || saving) {
      return;
    }

    setSaving(true);
    onClearError();

    try {
      await destroyUnavailability(destroyTarget.id);
      if (selection?.id === destroyTarget.id) {
        cancel();
      }
      setDestroyTarget(null);
      onReload();
    } catch (error) {
      onError(errorMessageFromResponse(error));
      setDestroyTarget(null);
    } finally {
      setSaving(false);
    }
  }, [cancel, destroyTarget, onClearError, onError, onReload, saving, selection?.id]);

  return (
    <div className="availabilities-management">
      {errorMessage && <Alert style="danger">{errorMessage}</Alert>}

      <div className="panel">
        <div className="panel-heading">
          <strong className="panel-title">Scheduling Rules</strong>
        </div>
        <div className="panel-body">
          <p>
            Users are fully <strong>available</strong> by default. Please select when this user is{' '}
            <strong>NOT available</strong> by the day of the week:
          </p>
        </div>
      </div>

      <RecurringTable
        unavailabilities={unavailabilities}
        form={form}
        selection={selection}
        saving={saving || loading}
        onAdd={() => add(true)}
        onManage={manage}
        onDestroy={setDestroyTarget}
        onCancel={cancel}
        onSave={save}
        onFormChange={setForm}
      />

      <NonrecurringTable
        unavailabilities={unavailabilities}
        form={form}
        selection={selection}
        saving={saving || loading}
        onAdd={() => add(false)}
        onManage={manage}
        onDestroy={setDestroyTarget}
        onCancel={cancel}
        onSave={save}
        onFormChange={setForm}
      />

      {unavailabilities && <ManagementCalendar unavailabilities={unavailabilities} />}

      {destroyTarget && (
        <AvailabilityConfirmationDialog
          onCancel={() => setDestroyTarget(null)}
          onConfirm={confirmDestroy}
          pending={saving}
        />
      )}
    </div>
  );
};
